import type { ClassSession } from "@/types/ClassSession";
import { create } from "zustand";

export type WidgetView =
  | "calendar"
  | "packages"
  | "login"
  | "password"
  | "profile"
  | "booking"
  | "private-booking";

const VIEW_ORDER: Record<WidgetView, number> = {
  calendar: 0,
  packages: 0,
  login: 1,
  password: 2,
  profile: 2,
  booking: 1,
  "private-booking": 0,
};

type NavigateOptions = {
  intended?: WidgetView;
  profileTab?: string;
};

type ViewState = {
  current: WidgetView;
  previous: WidgetView | null;
  direction: 1 | -1;
  intended?: WidgetView;
  profileTab: string | null;
  selectedEvent: ClassSession | null;
  bookingId: string | null;

  setSelectedEvent: (event: ClassSession | null, isConnected: boolean) => void;
  navigate: (view: WidgetView, opts?: NavigateOptions) => void;
  onBookingSuccess: (bookingId: string) => void;
  onLoginSuccess: () => void;
};

export const useViewStore = create<ViewState>((set, get) => ({
  current: "calendar",
  previous: null,
  selectedEvent: null,
  bookingId: null,
  profileTab: null,
  direction: 1,

  navigate: (target, opts) => {
    const current = get().current;

    const next = target;
    const direction = VIEW_ORDER[next] > VIEW_ORDER[current] ? 1 : -1;

    set({
      previous: current,
      current: next,
      intended: opts?.intended,
      profileTab: opts?.profileTab ?? null,
      direction,
    });
  },

  onLoginSuccess: () => {
    const { intended, current } = get();
    const next = intended ?? "calendar";

    const direction = VIEW_ORDER[next] > VIEW_ORDER[current] ? 1 : -1;

    set({
      previous: current,
      current: next,
      intended: undefined,
      direction,
    });
  },

  setSelectedEvent: (event, isConnected) =>
    set({
      selectedEvent: event,
      previous: get().current,
      current: isConnected ? "booking" : "login",
    }),

  onBookingSuccess: (bookingId) =>
    set({
      bookingId,
      previous: get().current,
      current: "profile",
    }),
}));
